import { formatMoney, type MoneyCommunity } from '@/lib/money'
import type { GoalCreatePermissions, GoalCycleStatus, GoalCycleSummary, GoalHealth, KeyResult, KeyResultOutcome, Objective, KeyResultDirection, KeyResultInput, KeyResultUnit, ObjectiveLevel } from '@/types/goals'
import { addMonths, dayIn, isDay } from '@/components/features/performanceReview/reviewUtils'

export { addMonths, apiErrors, dayIn, formatDay, formatRange, isDay, messageOf, reviewSpaceModeOf as goalsSpaceModeOf, statusOf } from '@/components/features/performanceReview/reviewUtils'

/** The API's limits (`goals.routes.js`). */
export const GOAL_LIMITS = { cycleTitle: 120, title: 200, description: 5000, note: 2000, keyResults: 20, weightMax: 100 } as const

export const LEVELS: ObjectiveLevel[] = ['COMPANY', 'GROUP', 'PERSONAL']
export const DIRECTIONS: KeyResultDirection[] = ['INCREASE', 'DECREASE', 'MAINTAIN', 'DELIVERABLE']
export const UNITS: KeyResultUnit[] = ['PERCENT', 'NUMBER', 'CURRENCY']
export const HEALTHS: GoalHealth[] = ['ON_TRACK', 'AT_RISK', 'OFF_TRACK']

export const HEALTH_DOT: Record<GoalHealth, string> = {
  ON_TRACK: 'bg-emerald-500',
  AT_RISK: 'bg-amber-400',
  OFF_TRACK: 'bg-red-500'
}
export const HEALTH_PILL: Record<GoalHealth, string> = {
  ON_TRACK: 'bg-emerald-50 text-emerald-700',
  AT_RISK: 'bg-amber-50 text-amber-700',
  OFF_TRACK: 'bg-red-50 text-red-700'
}
export const LEVEL_PILL: Record<ObjectiveLevel, string> = {
  COMPANY: 'bg-primary-50 text-primary-700',
  GROUP: 'bg-blue-50 text-blue-700',
  PERSONAL: 'bg-gray-100 text-gray-700'
}

/** A value in the KR's unit: the community's money for CURRENCY (PADROES-UI section 10). */
export const formatValue = (value: number | null | undefined, kr: Pick<KeyResult, 'unit' | 'currency'>, locale: string, community?: MoneyCommunity | null): string => {
  if (value === null || value === undefined) return '—'
  if (kr.unit === 'CURRENCY') return formatMoney(value, kr.currency, locale, community)
  const n = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value)
  return kr.unit === 'PERCENT' ? `${n}%` : n
}

export const pct = (n: number | null | undefined) => `${Math.round(Math.max(0, Math.min(100, n ?? 0)))}%`

/** A key result as typed in a form: numbers as strings until saved. */
export interface KeyResultDraft {
  key: string
  id?: string
  title: string
  ownerId: string
  ownerName: string
  unit: KeyResultUnit
  direction: KeyResultDirection
  initialValue: string
  targetValue: string
  minValue: string
  maxValue: string
  weight: string
}

let seq = 0
export const newKeyResultDraft = (owner: { id: string; name: string }): KeyResultDraft => ({
  key: `kr${Date.now().toString(36)}${(seq += 1)}`, title: '', ownerId: owner.id, ownerName: owner.name, unit: 'PERCENT', direction: 'INCREASE',
  initialValue: '0', targetValue: '100', minValue: '', maxValue: '', weight: '1'
})

export const draftOfKeyResult = (k: KeyResult): KeyResultDraft => ({
  key: k.id, id: k.id, title: k.title, ownerId: k.owner?.id || '', ownerName: k.owner?.name || '', unit: k.unit, direction: k.direction,
  initialValue: k.initialValue === null ? '' : String(k.initialValue), targetValue: k.targetValue === null ? '' : String(k.targetValue),
  minValue: k.minValue === null ? '' : String(k.minValue), maxValue: k.maxValue === null ? '' : String(k.maxValue), weight: String(k.weight ?? 1)
})

const toNum = (s: string): number | undefined => (s.trim() === '' ? undefined : Number(s.replace(',', '.')))

/** Field errors of a KR draft (keys `goals` namespace), `null` when valid: the same rules the API applies (422 `targetInvalid`). */
export const validateKeyResult = (d: KeyResultDraft, t: (k: string) => string): Partial<Record<keyof KeyResultDraft, string>> | null => {
  const e: Partial<Record<keyof KeyResultDraft, string>> = {}
  if (!d.title.trim()) e.title = t('kr.errors.title')
  const bad = (s: string) => s.trim() === '' || Number.isNaN(toNum(s))
  if (d.direction === 'INCREASE' || d.direction === 'DECREASE') {
    if (bad(d.initialValue)) e.initialValue = t('kr.errors.number')
    if (bad(d.targetValue)) e.targetValue = t('kr.errors.number')
    else if (!bad(d.initialValue)) {
      const a = toNum(d.initialValue) as number
      const b = toNum(d.targetValue) as number
      if (d.direction === 'INCREASE' && !(b > a)) e.targetValue = t('kr.errors.increase')
      if (d.direction === 'DECREASE' && !(b < a)) e.targetValue = t('kr.errors.decrease')
    }
  }
  if (d.direction === 'MAINTAIN') {
    if (bad(d.minValue)) e.minValue = t('kr.errors.number')
    if (bad(d.maxValue)) e.maxValue = t('kr.errors.number')
    else if (!bad(d.minValue) && (toNum(d.maxValue) as number) < (toNum(d.minValue) as number)) e.maxValue = t('kr.errors.range')
  }
  const w = toNum(d.weight)
  if (d.weight.trim() !== '' && (w === undefined || Number.isNaN(w) || w <= 0 || w > GOAL_LIMITS.weightMax)) e.weight = t('kr.errors.weight')
  return Object.keys(e).length ? e : null
}

/** The body the API takes; values that do not apply to the type are left out. */
export const keyResultBody = (d: KeyResultDraft): KeyResultInput => {
  const base: KeyResultInput = { title: d.title.trim(), direction: d.direction, ...(d.ownerId ? { ownerId: d.ownerId } : {}), ...(toNum(d.weight) ? { weight: toNum(d.weight) } : {}) }
  if (d.direction === 'DELIVERABLE') return base
  base.unit = d.unit
  if (d.direction === 'MAINTAIN') return { ...base, minValue: toNum(d.minValue), maxValue: toNum(d.maxValue) }
  return { ...base, initialValue: toNum(d.initialValue), targetValue: toNum(d.targetValue) }
}

/** The KR's goal in one line ("0% → 100%", "60–100", "entregável"). */
export const goalLine = (k: KeyResult, locale: string, t: (k: string, o?: Record<string, unknown>) => string, community?: MoneyCommunity | null) => {
  if (k.direction === 'DELIVERABLE') return t('kr.deliverableGoal')
  if (k.direction === 'MAINTAIN') return t('kr.maintainGoal', { min: formatValue(k.minValue, k, locale, community), max: formatValue(k.maxValue, k, locale, community) })
  return `${formatValue(k.initialValue, k, locale, community)} → ${formatValue(k.targetValue, k, locale, community)}`
}

/** A new cycle: from today, three months (all editable). */
export const emptyGoalCycle = (timeZone: string | undefined, title: string) => {
  const today = dayIn(timeZone)
  return { title, startsOn: today, endsOn: addMonths(today, 3) }
}

export interface GoalCycleDraft { title: string; startsOn: string; endsOn: string }

export const validateGoalCycle = (d: GoalCycleDraft, t: (k: string) => string): Partial<Record<keyof GoalCycleDraft, string>> | null => {
  const e: Partial<Record<keyof GoalCycleDraft, string>> = {}
  if (!d.title.trim()) e.title = t('cycleForm.errors.title')
  if (!isDay(d.startsOn)) e.startsOn = t('cycleForm.errors.day')
  if (!isDay(d.endsOn)) e.endsOn = t('cycleForm.errors.day')
  else if (isDay(d.startsOn) && d.endsOn < d.startsOn) e.endsOn = t('cycleForm.errors.period')
  return Object.keys(e).length ? e : null
}

/** Every objective of a tree, flat. */
export const flattenTree = (nodes: Objective[]): Objective[] => nodes.flatMap((n) => [n, ...flattenTree(n.children || [])])

export const NO_CREATE: GoalCreatePermissions = { company: false, groupIds: [], personalFor: [], anyPerson: false }

/** May the viewer create an objective of this level (the cycle's `canCreate`, from the server)? */
export const allows = (p: GoalCreatePermissions, level: ObjectiveLevel) =>
  level === 'COMPANY' ? p.company : level === 'GROUP' ? p.groupIds.length > 0 : p.anyPerson || p.personalFor.length > 0

/** May the viewer create any objective at all in this cycle? */
export const canCreateAny = (p: GoalCreatePermissions | undefined) => Boolean(p && (p.company || p.groupIds.length || p.personalFor.length || p.anyPerson))

/**
 * How a key result ended (or stands): MET at 100% or more, PARTIAL from 30%,
 * MISSED below 30%, NO_CHECK_IN before its first check-in. The progress is the
 * server's (`keyResultProgress`); only the bands are drawn here.
 */
export const OUTCOMES: KeyResultOutcome[] = ['MET', 'PARTIAL', 'MISSED', 'NO_CHECK_IN']
export const OUTCOME_BANDS = { met: 100, partial: 30 } as const
export const OUTCOME_BAR: Record<KeyResultOutcome, string> = {
  MET: 'bg-emerald-500',
  PARTIAL: 'bg-amber-400',
  MISSED: 'bg-red-500',
  NO_CHECK_IN: 'bg-gray-300'
}
/** The API's OKR band (`scoreBand`) when it sends one; otherwise the progress bands above. */
export const outcomeOf = (k: Pick<KeyResult, 'progress' | 'lastCheckInAt' | 'scoreBand'>): KeyResultOutcome =>
  !k.lastCheckInAt ? 'NO_CHECK_IN' : k.scoreBand ? k.scoreBand : k.progress >= OUTCOME_BANDS.met ? 'MET' : k.progress >= OUTCOME_BANDS.partial ? 'PARTIAL' : 'MISSED'

/** The OKR grade bands (0.0 to 1.0): met from 0.7, in progress from 0.4, missed below. */
export const SCORE_BANDS = { met: 0.7, progress: 0.4 } as const
export type ScoreBand = Exclude<KeyResultOutcome, 'NO_CHECK_IN'>
export const SCORE_PILL: Record<ScoreBand, string> = {
  MET: 'bg-emerald-50 text-emerald-700',
  PARTIAL: 'bg-amber-50 text-amber-700',
  MISSED: 'bg-red-50 text-red-700'
}
export const SCORE_TEXT: Record<ScoreBand, string> = {
  MET: 'text-emerald-700',
  PARTIAL: 'text-amber-700',
  MISSED: 'text-red-700'
}
/** The summary's OKR grade, `null` while the API does not send one. */
export const scoreOf = (s: Pick<GoalCycleSummary, 'score'> | null | undefined): number | null =>
  typeof s?.score === 'number' && Number.isFinite(s.score) ? Math.max(0, Math.min(1, s.score)) : null
export const scoreBand = (score: number): ScoreBand => (score >= SCORE_BANDS.met ? 'MET' : score >= SCORE_BANDS.progress ? 'PARTIAL' : 'MISSED')
/** "0,67" / "0.67": always two decimals, in the viewer's locale. */
export const formatScore = (score: number, locale: string) =>
  new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(score)

/** A cycle's result (`GoalCycleSummary`, in `@/types/goals`) from the objectives the viewer reads. */
export const summarizeObjectives = (objectives: Objective[]): GoalCycleSummary => {
  const outcomes: Record<KeyResultOutcome, number> = { MET: 0, PARTIAL: 0, MISSED: 0, NO_CHECK_IN: 0 }
  const health: Record<GoalHealth, number> = { ON_TRACK: 0, AT_RISK: 0, OFF_TRACK: 0 }
  const scored = objectives.filter((o) => o.keyResults.length > 0)
  let keyResults = 0
  for (const o of objectives) {
    for (const k of o.keyResults) {
      keyResults += 1
      outcomes[outcomeOf(k)] += 1
      if (k.health) health[k.health] += 1
    }
  }
  const progress = scored.length ? Math.round((scored.reduce((s, o) => s + o.progress, 0) / scored.length) * 10) / 10 : null
  // The OKR score only when the API scores every objective (the cycle's score is their plain average).
  const graded = scored.length > 0 && scored.every((o) => typeof o.score === 'number')
  const score = graded ? Math.round((scored.reduce((s, o) => s + (o.score as number), 0) / scored.length) * 100) / 100 : undefined
  return { progress, ...(score === undefined ? {} : { score }), objectives: objectives.length, keyResults, outcomes, health }
}

/** The KR's real value against its goal: "12 de 20", "R$ 45 mil de R$ 60 mil", "Entregue". */
export const resultLine = (k: KeyResult, locale: string, t: (k: string, o?: Record<string, unknown>) => string, community?: MoneyCommunity | null) => {
  if (k.direction === 'DELIVERABLE') return k.done ? t('history.done') : t('history.notDone')
  const current = formatValue(k.currentValue, k, locale, community)
  if (k.direction === 'MAINTAIN') return t('kr.ofRange', { current, min: formatValue(k.minValue, k, locale, community), max: formatValue(k.maxValue, k, locale, community) })
  return t('kr.ofTarget', { current, target: formatValue(k.targetValue, k, locale, community) })
}

/**
 * "8 KRs · 5 atingidos · 2 parciais · 1 não atingido": closed cycles by
 * outcome, open ones by the last check-in's light (only counts above zero).
 */
export const summaryCounts = (summary: GoalCycleSummary, status: GoalCycleStatus, t: (k: string, o?: Record<string, unknown>) => string) => {
  const parts = [t('summary.keyResults', { count: summary.keyResults })]
  if (status === 'CLOSED') {
    for (const o of OUTCOMES) if (summary.outcomes[o] > 0) parts.push(t(`summary.outcome.${o}`, { count: summary.outcomes[o] }))
  } else {
    for (const h of HEALTHS) if (summary.health[h] > 0) parts.push(t(`summary.health.${h}`, { count: summary.health[h] }))
    if (summary.outcomes.NO_CHECK_IN > 0) parts.push(t('summary.outcome.NO_CHECK_IN', { count: summary.outcomes.NO_CHECK_IN }))
  }
  return parts.join(' · ')
}
